import { useState } from "react";
import { AppendDrawer } from "@/app/AppendDrawer";
import { currencyRates, type CurrencyRate, type Input } from "@/app/master";
import { useCanWrite } from "@/app/auth";
import { loadingOf, useList } from "@/app/queries";
import { Alert, Button, EmptyState, formatMoney, formatTime, PageHeader, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { rateDraft, rateInput } from "./costDraft";
import { RateForm } from "./CostForms";

/** 汇率管理：每个币种到基准币种的版本，只增不改（决策 D5）。 */
export function CurrencyRates() {
  const canWrite = useCanWrite();
  const [adding, setAdding] = useState(false);
  const list = useList("currency-rates", currencyRates);
  const rows = list.data ?? [];
  return (
    <>
      <PageHeader title="汇率管理" description="各币种到基准币种的汇率及生效时间，只增不改；时间按北京时间。" actions={canWrite && <Button variant="primary" onClick={() => setAdding(true)}>新增汇率</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar right={<ToolbarCount>{rows.length} 条</ToolbarCount>}><span className="ax-secondary">1 原币等于多少基准币；基准币自身也要有一条汇率为 1 的记录。</span></Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {rows.length ? (
          <Table>
            <thead><tr><Th w="id">ID</Th><Th w={72}>币种</Th><Th numeric w="money">汇率</Th><Th numeric w={160}>生效时间</Th><Th>操作者</Th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="ax-mono">{r.id}</td><td className="ax-mono">{r.currency}</td><td className="ax-num ax-mono">{formatMoney(r.rate, undefined, 6)}</td>
                  <td className="ax-num ax-mono">{formatTime(r.effective_from, false)}</td><td className="ax-secondary">{r.created_by}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !list.isPending && <EmptyState title="没有汇率">没有汇率的币种无法计价，消息会被拒绝。</EmptyState>
        )}
      </TableWrap>
      {adding && <RateDrawer onClose={() => setAdding(false)} />}
    </>
  );
}

function RateDrawer({ onClose }: { onClose: () => void }) {
  return (
    <AppendDrawer
      noun="汇率"
      initial={rateDraft()}
      convert={rateInput}
      save={(body: Input<CurrencyRate>) => currencyRates.create(body)}
      invalidate={["currency-rates"]}
      message="已新增汇率"
      note="汇率只增不改，生效后新的价格快照按它折算基准币。此操作写入审计日志。"
      summary={(body) => [
        { label: "币种", value: body.currency, mono: true },
        { label: "汇率", value: formatMoney(body.rate, undefined, 6), mono: true },
        { label: "生效时间", value: `${formatTime(body.effective_from)} 北京时间`, mono: true },
      ]}
      onClose={onClose}
    >
      {(form) => <RateForm draft={form.draft} errors={form.errors} set={form.set} />}
    </AppendDrawer>
  );
}
